import { createSlice } from '@reduxjs/toolkit';

const shouldClearValue = (value: unknown): boolean => value === false || (value !== 0 && !value);

const handleDataOperations = (state: any, action: any, stateKey: string): void => {
    const payload = action.payload;
    const { filterType, selectorKey, filterKey, value } = payload;
    let selectorState = state[stateKey][selectorKey];

    switch (filterType) {
        case 'input-text':
        case 'calendar':
        case 'single-select':
        case 'multi-chips': {
            if (!selectorState) {
                if (shouldClearValue(value)) {
                    break;
                }

                selectorState = state[stateKey][selectorKey] = {
                    [filterKey]: value,
                };
            } else if (shouldClearValue(value)) {
                delete selectorState[filterKey];
            } else {
                selectorState[filterKey] = value;
            }

            if (!selectorState?.[filterKey]?.length && filterType === 'multi-chips') {
                delete selectorState[filterKey];
            }
            break;
        }
        case 'multi-select': {
            if (!selectorState) {
                selectorState = state[stateKey][selectorKey] = {
                    [filterKey]: Array.isArray(value) ? value : [value],
                };
            } else if (!selectorState[filterKey]) {
                selectorState[filterKey] = Array.isArray(value) ? value : [value];
            } else if (Array.isArray(value)) {
                selectorState[filterKey] = value;
            } else {
                const filterValues = selectorState[filterKey];
                const filterIndex = filterValues.findIndex((item: string) => item === value);
                if (filterIndex !== -1) {
                    filterValues.splice(filterIndex, 1);
                } else {
                    filterValues.push(value);
                }
            }

            if (!selectorState[filterKey].length) {
                delete selectorState[filterKey];
            }
            break;
        }
        case 'multi-calendar': {
            if (!selectorState) {
                selectorState = state[stateKey][selectorKey] = {};
            }

            if (shouldClearValue(value)) {
                if (filterKey === 'from' || filterKey === 'to') {
                    const hasFromDate = selectorState['from'];
                    const hasToDate = selectorState['to'];

                    if (!hasFromDate || !hasToDate) {
                        delete selectorState['from'];
                        delete selectorState['to'];
                    } else {
                        delete selectorState[filterKey];
                    }
                }
            } else {
                selectorState[filterKey] = value;

                if (filterKey === 'from' && !selectorState['to']) {
                    selectorState['to'] = '';
                } else if (filterKey === 'to' && !selectorState['from']) {
                    selectorState['from'] = '';
                }
            }

            if (!selectorState['from'] && !selectorState['to']) {
                delete state[stateKey][selectorKey];
            }
            break;
        }

        default: {
            break;
        }
    }
};

const handleResetApplyOperations = (state: any, action: any, preStateKey: string, stateKey: string): void => {
    const payload = action.payload;
    const { type, selectorKey } = payload;

    if (type === 'RESET') {
        delete state[preStateKey][selectorKey];
        delete state[stateKey][selectorKey];
    } else {
        state[stateKey][selectorKey] = state[preStateKey][selectorKey];
    }
};

const dataOperationsSlice = createSlice({
    name: 'dataOperations',
    initialState: {
        preSortValues: {},
        preFilterValues: {},
        selectedFilterValues: {},
        selectedSortValues: {},
        availableOptions: {},
        searchKey: {},
    } as any,
    reducers: {
        handleRemoveFilter: (state, action) => {
            const payload = action.payload;
            const { selectorKey, filterKey } = payload ?? {};

            if (
                selectorKey &&
                filterKey &&
                Object.prototype.hasOwnProperty.call(state.preFilterValues?.[selectorKey] ?? {}, filterKey)
            ) {
                if (filterKey === 'from') {
                    delete state.preFilterValues[selectorKey][filterKey];
                    delete state.preFilterValues[selectorKey]['to'];
                    delete state.selectedFilterValues[selectorKey][filterKey];
                    delete state.selectedFilterValues[selectorKey]['to'];
                } else {
                    delete state.preFilterValues[selectorKey][filterKey];
                    delete state.selectedFilterValues[selectorKey];
                }
            }
        },
        handleChangePreFilterValue: (state, action) => {
            handleDataOperations(state, action, 'preFilterValues');
        },
        handleResetPreFilterValue: (state, action) => {
            handleResetApplyOperations(state, action, 'selectedFilterValues', 'preFilterValues');
        },
        handleSetAvailableOptions: (state, action) => {
            const payload = action.payload;
            const { selectorKey, fieldKey, options } = payload;
            let selectorOptions = state.availableOptions[selectorKey];

            if (!selectorOptions) {
                if (!options.length) {
                    return;
                }

                selectorOptions = state.availableOptions[selectorKey] = {
                    [fieldKey]: options,
                };
            } else if (options.length === 0) {
                delete selectorOptions[fieldKey];
            } else {
                selectorOptions[fieldKey] = options;
            }
        },
        handleChangeSortValue: (state, action) => {
            const payload = action.payload;
            const { selectorKey, key, value } = payload;

            if (!state.preSortValues[selectorKey]) {
                state.preSortValues[selectorKey] = {
                    [key]: value,
                };
            } else {
                state.preSortValues[selectorKey][key] = value;
            }
        },
        handleApplyResetSorting: (state, action) => {
            handleResetApplyOperations(state, action, 'preSortValues', 'selectedSortValues');
        },
        handleApplyResetFiltering: (state, action) => {
            handleResetApplyOperations(state, action, 'preFilterValues', 'selectedFilterValues');
        },
        handleGlobalSearch: (state, action) => {
            const payload = action.payload;
            const { selectorKey, value } = payload;

            if (value === '') {
                delete state.searchKey[selectorKey];
            } else {
                state.searchKey[selectorKey] = value;
            }
        },
    },
});

export const {
    handleRemoveFilter,
    handleChangePreFilterValue,
    handleResetPreFilterValue,
    handleSetAvailableOptions,
    handleChangeSortValue,
    handleApplyResetSorting,
    handleApplyResetFiltering,
    handleGlobalSearch,
} = dataOperationsSlice.actions;

export default dataOperationsSlice;
